// The server's "something changed" signal (WEB_PLAN.md §3.4). Database triggers send a private Realtime broadcast on
// the topic `company:<id>` (supabase/migrations/20261008120000_groundteam_doorbell.sql):
//
// - `changed` {seq}: after each transaction that wrote sync_items. `seq` is the server_seq of one row it wrote, normally
//   its first (after an owner change, the first dependent it re-emitted, above the record's own). The transaction's
//   rows commit together under the company lock, so once a pull has brought the cursor to `seq`, it has them all.
// - `team` {}: members joined, left or changed role, or the visibility epoch or company name changed.
//
// A ring carries no row data. The engine answers it with an ordinary sync_pull, so the server's read rules still
// decide what each person gets, and polling stays as the fallback when the channel is down.
//
// - The channel is private: only active members of the company may read the topic (an RLS policy on
//   realtime.messages), so the join must carry the user's JWT, not the anon key. The token is set before subscribing.
//   After that supabase-js 2.117 keeps it current by itself: it calls `realtime.setAuth` on SIGNED_IN /
//   TOKEN_REFRESHED and asks the session for the token again on every heartbeat (25 s).
// - SUBSCRIBED (the first join and every rejoin) rings once without a seq, so the engine pulls whatever was saved
//   while the channel was down.
// - CHANNEL_ERROR / TIMED_OUT: supabase-js rejoins with its own backoff; this only reports "not live" so the engine
//   polls faster meanwhile. A channel the server CLOSED (an expired token, say) is not rejoined by supabase-js: it is
//   reopened here after REOPEN_AFTER_MS, doubling up to REOPEN_MAX_MS.

import { REALTIME_SUBSCRIBE_STATES, type RealtimeChannel, type SupabaseClient } from '@supabase/supabase-js';

export type RingKind = 'changed' | 'team';

/** First reopen of a channel the server closed; each further try waits twice as long, up to REOPEN_MAX_MS. */
export const REOPEN_AFTER_MS = 30_000;
export const REOPEN_MAX_MS = 5 * 60_000;

/** The topic the triggers send to and the read policy checks: `company:<lower-case id>`. */
export function doorbellTopic(companyId: string): string {
  return `company:${companyId.toLowerCase()}`;
}

/** The `seq` of a `changed` ring; null when the payload has none (the engine then pulls regardless). */
export function ringSeq(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const seq = (payload as { seq?: unknown }).seq;
  if (typeof seq === 'number') return Number.isSafeInteger(seq) && seq >= 0 ? seq : null;
  // A bigint may arrive as a string from other senders.
  if (typeof seq === 'string' && /^\d{1,15}$/.test(seq)) return Number(seq);
  return null;
}

/**
 * Listens for rings on the company's channel until the returned stop is called. `onRing` gets each ring (and one
 * `changed` ring without a seq on every SUBSCRIBED); `onState` says whether the channel is subscribed. Neither runs
 * after stop.
 */
export function listenForChanges(
  client: SupabaseClient,
  companyId: string,
  onRing: (kind: RingKind, seq: number | null) => void,
  onState: (live: boolean) => void,
): () => void {
  const topic = doorbellTopic(companyId);
  let stopped = false;
  let channel: RealtimeChannel | null = null;
  let live = false;
  let reopenTimer: ReturnType<typeof setTimeout> | null = null;
  let reopenDelay = REOPEN_AFTER_MS;

  const setLive = (next: boolean) => {
    if (stopped || live === next) return;
    live = next;
    onState(next);
  };

  const reopenLater = () => {
    if (stopped || reopenTimer) return;
    const wait = reopenDelay;
    reopenDelay = Math.min(reopenDelay * 2, REOPEN_MAX_MS);
    reopenTimer = setTimeout(() => {
      reopenTimer = null;
      open();
    }, wait);
  };

  const subscribe = async () => {
    // The join sends whatever token the client holds at that moment: make sure it is this user's session.
    await client.realtime.setAuth();
    if (stopped) return;
    // channel() hands back an existing channel for the same topic, and one still leaving (a StrictMode remount, a quick
    // close and reopen) never rejoins: let it go first.
    const stale = client.getChannels().find((c) => c.topic === `realtime:${topic}`);
    if (stale) await client.removeChannel(stale);
    if (stopped) return;
    const current = () => !stopped && channel === ch;
    const ch = client
      .channel(topic, { config: { private: true } })
      .on('broadcast', { event: 'changed' }, (message) => {
        if (current()) onRing('changed', ringSeq(message.payload));
      })
      .on('broadcast', { event: 'team' }, () => {
        if (current()) onRing('team', null);
      });
    channel = ch;
    ch.subscribe((status) => {
      if (!current()) return;
      if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
        reopenDelay = REOPEN_AFTER_MS;
        setLive(true);
        onRing('changed', null); // catch up on what was saved while the channel was down
      } else if (status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
        channel = null;
        setLive(false);
        reopenLater();
      } else {
        // CHANNEL_ERROR, TIMED_OUT: supabase-js rejoins by itself.
        setLive(false);
      }
    });
  };

  function open(): void {
    if (stopped) return;
    subscribe().catch((error: unknown) => {
      // No WebSocket, a blocked connection: the engine keeps polling.
      console.warn('Live updates unavailable; checking for changes on a timer instead.', error);
      channel = null;
      setLive(false);
      reopenLater();
    });
  }

  open();
  return () => {
    if (stopped) return;
    stopped = true;
    if (reopenTimer) clearTimeout(reopenTimer);
    reopenTimer = null;
    const ch = channel;
    channel = null;
    if (ch) void client.removeChannel(ch).catch(() => undefined);
  };
}
